// Shared identifier helpers for the alias / CTE scope resolvers. The
// tokenizer keeps the surrounding quote characters on a quoted identifier
// (`[id]`, `"id"`, `` `id` ``) so the position-kind machine can compare via
// `toUpperCase()` without casing surprises. The scope resolvers, however,
// need the *content* of the identifier — un-quoted, un-escaped — because
// downstream consumers (the schema bus, the resolver) match on bare names.

export type ParsedIdentifier = {
  readonly content: string;
  readonly quoted: boolean;
};

export function parseIdentifierToken(text: string): ParsedIdentifier {
  if (text.length === 0) return { content: '', quoted: false };
  const first = text.charCodeAt(0);
  if (first === 0x5b /* [ */ && text.charCodeAt(text.length - 1) === 0x5d /* ] */) {
    return { content: text.slice(1, -1).replace(/]]/g, ']'), quoted: true };
  }
  if (first === 0x22 /* " */ && text.charCodeAt(text.length - 1) === 0x22) {
    return { content: text.slice(1, -1).replace(/""/g, '"'), quoted: true };
  }
  if (first === 0x60 /* ` */ && text.charCodeAt(text.length - 1) === 0x60) {
    return { content: text.slice(1, -1).replace(/``/g, '`'), quoted: true };
  }
  return { content: text, quoted: false };
}

/**
 * Identifier comparison used by the alias / CTE scope resolvers when
 * checking aliases against keyword sets or comparing two identifiers
 * for equality. Bare identifiers are case-insensitive in every dialect we
 * target; quoted identifiers are technically case-sensitive in PG / Oracle
 * but bare-name resolvers fold the quote-aware comparison into the
 * specific case-sensitive lookup at the snapshot layer (next slice).
 */
export function identifiersEqualCi(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}
